"use client";

import { BorrowerLogin, useWeb3 } from "logic/components";
import { cn } from "logic/utils";
import { type ReactNode, Suspense } from "react";

import { ActiveLoansSection } from "./ActiveLoansSection";
import { LoanDetails } from "./LoanDetails";
import { LoansPageProvider, UnauthorizedLoansError, useLoansPage } from "./LoansPageProvider";
import { OverdueAlerts } from "./OverdueAlerts";
import { WelcomeTitle } from "./WelcomeTitle";

export function LoansPage() {
  return (
    // `LoansPageProvider` reads `?loan=` through `useSearchParams`, which suspends.
    <Suspense>
      <LoansPageProvider>
        <LoansPageContent />
      </LoansPageProvider>
    </Suspense>
  );
}

function LoansPageContent() {
  const {
    account: { ready, address },
  } = useWeb3();

  const { loans, loansError, selectedLoan } = useLoansPage();

  if (!ready) return <Notice>Loading...</Notice>;

  // Full-screen: `LoansChrome` drops the shell while no wallet is connected.
  if (!address) return <BorrowerLogin />;

  if (!loans) {
    if (loansError instanceof UnauthorizedLoansError)
      return <Notice className="text-sys-negative-negative">Unauthorized</Notice>;
    if (loansError) return <Notice className="text-sys-negative-negative">Something went wrong</Notice>;
    return <Notice>Loading...</Notice>;
  }

  // Still the un-reskinned detail view, and `LoansChrome` gives it the old
  // layout rather than this page's shell.
  if (selectedLoan) return <LoanDetails />;

  return (
    <>
      <WelcomeTitle />

      <OverdueAlerts loans={loans} />

      <ActiveLoansSection loans={loans} />
    </>
  );
}

type NoticeProps = {
  children: ReactNode;
  className?: string;
};

/** The page's loading and error states. No frame covers either, so they stay
 *  a single centred line, as the old loans page does. */
function Notice(props: NoticeProps) {
  const { children, className } = props;

  return <p className={cn("text-body-lg-400 m-auto text-surface-on-surface-variant", className)}>{children}</p>;
}
